/** @format */
import { CancelToken } from 'axios';
import { TFunction } from 'i18next';
import { omit } from 'lodash';
import { stringify } from 'qs';

import {
    centToDecimal,
    formatAsCurrency,
} from '@toucan/client/global/currency';
import {
    Market,
    MarketCode,
    defaultMarket,
    euMarket,
    europeanMarkets,
} from '@toucan/client/global/market';
import {
    ItemId,
    Product,
    ProductConditionValue,
    formatPriceModifiers,
    getProductAttributes,
    getProductConditionDescription,
    getProductIncludesList,
} from '@toucan/client/global/product';

import { QuoteProductCondition } from '../config/types';
import {
    fetchQuoteBuyProducts,
    fetchQuoteProductConditions,
} from '../requests/search';

type GetQuoteProductConditionsArgs = {
    /**
     * The model id to search against
     */
    modelId: string;

    /**
     * The locale to search in. E.g. en_GB
     */
    locale?: string;

    /**
     * The market code
     */
    market: MarketCode;

    /**
     * An Axios cancel token
     */
    cancelToken?: CancelToken;

    skuBlocklist?: ItemId[];
};

type QuoteBuyProductsFilterQuery = {
    object_type: string;
    model_id: string;
    product_market: MarketCode;
    product_condition?: ProductConditionValue;
    product_sku?: string;
};

/**
 * Gets product conditions for adding a product to buy quote
 *
 * @param modelId {string} The model id to search against
 * @param cancelToken {string} The cancel token
 * @param locale {string} The locale to search in. E.g. en_GB
 * @param market {MarketCode} The market to search in
 */
export async function getQuoteProductConditions(
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    {
        modelId,
        cancelToken,
        locale = defaultMarket.iso,
        market = defaultMarket.code,
        skuBlocklist = [],
    }: GetQuoteProductConditionsArgs
): Promise<QuoteProductCondition[]> {
    const conditions = [
        {
            value: ProductConditionValue.LIKE_NEW,
            starRating: '5',
            text: t('Like new'),
        },
        {
            value: ProductConditionValue.EXCELLENT,
            starRating: '4',
            text: t('Excellent'),
        },
        {
            value: ProductConditionValue.GOOD,
            starRating: '3',
            text: t('Good'),
        },
        {
            value: ProductConditionValue.WELL_USED,
            starRating: '2',
            text: t('Well used'),
        },
        {
            value: ProductConditionValue.HEAVILY_USED,
            starRating: '1',
            text: t('Heavily used'),
        },
    ];

    // eslint-disable-next-line @mpb-com/mpb/todo-requires-ticket -- Legacy code: do not use as an example.
    // TODO - update when modelOperation is added to search-service
    // overrides the product/model (sets to EU) market for EU markets only.
    const productMarket = europeanMarkets.includes(market)
        ? euMarket.code
        : market;

    const filterQuery: QuoteBuyProductsFilterQuery = {
        object_type: 'PRODUCT',
        model_id: modelId,
        product_market: productMarket,
    };

    if (skuBlocklist.length) {
        filterQuery.product_sku = `NOT (${skuBlocklist.join(' OR ')})`;
    }

    const queryParams = stringify(
        {
            filter_query: filterQuery,
            rows: 0,
            facet_field: 'product_condition_star_rating.product_price',
        },
        {
            encodeValuesOnly: true,
            arrayFormat: 'repeat',
        }
    );

    const data = await fetchQuoteProductConditions(
        queryParams,
        cancelToken,
        locale
    );

    const results = data.facets?.[0]?.values ?? [];

    return conditions.map((condition) => {
        const result = results.find(
            ({ fieldValue }) => fieldValue === condition.starRating
        );
        const mappedConditionData: QuoteProductCondition = omit(
            condition,
            'starRating'
        );

        if (!result || result?.count === '0') {
            mappedConditionData.outOfStock = true;
        } else {
            mappedConditionData.minPrice = parseInt(result.minimum);
            mappedConditionData.maxPrice = parseInt(result.maximum);
        }

        return mappedConditionData;
    });
}

type GetQuoteBuyProductsArgs = {
    /**
     * Translation function
     */
    t: TFunction;

    /**
     * The active market from context
     */
    activeMarket: Market;

    /**
     * Condition of the product to search for
     */
    condition: ProductConditionValue;

    /**
     * The model id to search for products from
     */
    modelId: string;

    /**
     * Max number of results to return
     */
    limit?: number;

    /**
     * Index of the first result to return
     */
    start?: number;

    /**
     * An Axios cancel token
     */
    cancelToken?: CancelToken;

    /**
     * Array of blacklisted SKUs to exclude from search results
     */
    skuBlacklist?: ItemId[];
};

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type GetQuoteBuyProductsResponse = {
    products: Product[];
    totalResults: number;
};

export async function getQuoteBuyProducts({
    t,
    activeMarket,
    condition,
    modelId,
    cancelToken,
    limit = 5,
    start = 0,
    skuBlacklist = [],
}: GetQuoteBuyProductsArgs): Promise<GetQuoteBuyProductsResponse> {
    // eslint-disable-next-line @mpb-com/mpb/todo-requires-ticket -- Legacy code: do not use as an example.
    // TODO - update when modelOperation is added to search-service
    // overrides the product/model (sets to EU) market for EU markets only.
    const productMarket = europeanMarkets.includes(activeMarket.code)
        ? euMarket.code
        : activeMarket.code;

    const filterQuery: QuoteBuyProductsFilterQuery = {
        object_type: 'PRODUCT',
        model_id: modelId,
        product_condition: condition,
        product_market: productMarket,
    };

    if (skuBlacklist.length) {
        filterQuery.product_sku = `NOT (${skuBlacklist.join(' OR ')})`;
    }

    const queryParams = stringify(
        {
            filter_query: filterQuery,
            field_list: [
                'model_id',
                'model_name',
                'model_url_segment',
                'product_images',
                'product_price',
                'product_sku',
                'product_id',
                'product_url_segment',
                'product_shutter_count',
                'product_hour_count',
                'product_battery_charge_count',
                'product_price_modifiers',
                'model_available_new',
                'product_observation_count',
            ],
            start,
            rows: limit,
        },
        {
            encodeValuesOnly: true,
            arrayFormat: 'repeat',
        }
    );

    const data = await fetchQuoteBuyProducts(
        queryParams,
        cancelToken,
        activeMarket.iso
    );

    const formatWithCurrency = formatAsCurrency(
        activeMarket.iso,
        activeMarket.currency
    );

    const products = data.results.map((result) => {
        const price = formatWithCurrency(
            centToDecimal(parseInt(result.productPrice.values[0], 10)),
            { trailingZeros: false }
        );

        const shutterCount = result?.productShutterCount
            ? parseInt(result?.productShutterCount?.values[0], 10)
            : undefined;

        const hourCount = result?.productHourCount
            ? parseInt(result?.productHourCount?.values[0], 10)
            : undefined;

        const batteryChargeCount = result?.productBatteryChargeCount
            ? parseInt(result?.productBatteryChargeCount.values[0], 10)
            : undefined;

        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const modelAvailableNew =
            result?.modelAvailableNew?.values[0] === 'True';

        const productPriceModifiers =
            result?.productPriceModifiers?.values ?? [];

        const description = getProductIncludesList(
            t,
            productPriceModifiers,
            modelAvailableNew,
            condition
        );

        const conditionDescription = getProductConditionDescription({
            t,
            locale: activeMarket.iso,
            shutterCount,
            hourCount,
            batteryChargeCount,
        });

        const attributes = getProductAttributes({
            t,
            shutterCount,
            hourCount,
            batteryChargeCount,
            locale: activeMarket.iso,
            condition,
        });

        const productSlug = t('product', { ns: 'urls' });

        const fullLink = `/${activeMarket.slug}/${productSlug}/${result?.modelUrlSegment?.values[0]}/${result?.productUrlSegment?.values[0]}`;

        const priceModifiers = formatPriceModifiers(productPriceModifiers);

        return {
            price,
            description,
            condition,
            conditionDescription,
            attributes,
            id: parseInt(result?.productId?.values[0]) || 0,
            value: parseInt(result?.productPrice?.values[0]),
            sku: result?.productSku?.values[0],
            name: result?.modelName?.values[0],
            image: result?.productImages?.values[0],
            modelId: parseInt(result?.modelId?.values[0]),
            linkHref: fullLink,
            priceModifiers,
            productObservationCount:
                parseInt(result?.productObservationCount?.values[0]) || 0,
        };
    });

    return {
        products,
        totalResults: data.totalResults,
    };
}
